"use client";

import { ReactNode, useState } from "react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { GrLanguage } from "react-icons/gr";
import { useTranslations } from "next-intl";
import { useChangeLocale } from "@/hooks/locale/useChangeLocale";
import { LocaleMap, LocaleType } from "@/hooks/locale/useChangeLocale/types";
import { useStrictLocale } from "@/hooks/locale/useStrictLocale/useStrictLocale";
import S from "./index.module.scss";

const localeLabels: LocaleMap<{ title: string; description: string; short: string }> = {
  en: {
    title: "English",
    description: "Default interface language",
    short: "EN",
  },
  es: {
    title: "Español",
    description: "Spanish interface",
    short: "ES",
  },
  ru: {
    title: "Русский",
    description: "Russian interface",
    short: "RU",
  },
};

const localeOrder: LocaleType[] = ["ru", "es", "en"];

interface LanguageSwitcherProps {
  trigger?: ReactNode;
  triggerVariant?: "icon" | "languageName";
}

const LanguageSwitcher = ({ trigger, triggerVariant = "icon" }: LanguageSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const t = useTranslations("header.languageSwitcher");
  const locale = useStrictLocale();
  const { changeLocale, isPending } = useChangeLocale();
  const currentLocaleTitle = localeLabels[locale].title;

  const handleChangeLocale = (nextLocale: LocaleType) => {
    if (nextLocale === locale) {
      setOpen(false);
      return;
    }

    changeLocale(nextLocale);
    setOpen(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        {trigger || (
          <Button
            variant="soft"
            size="2"
            className={triggerVariant === "languageName" ? S.languageNameTrigger : S.trigger}
            aria-label={t("triggerAriaLabel", { language: currentLocaleTitle })}
          >
            {triggerVariant === "languageName" ? (
              currentLocaleTitle
            ) : (
              <GrLanguage className={S.triggerIcon} aria-hidden="true" />
            )}
          </Button>
        )}
      </Dialog.Trigger>

      <Dialog.Content maxWidth="420px" onOpenAutoFocus={event => event.preventDefault()}>
        <Dialog.Title>{t("title")}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {t("description")}
        </Dialog.Description>

        <Flex direction="column" gap="3">
          {localeOrder.map(item => {
            const itemMeta = localeLabels[item];
            const isActive = item === locale;

            return (
              <button
                key={item}
                type="button"
                className={S.option}
                onClick={() => handleChangeLocale(item)}
                disabled={isPending}
                data-active={isActive}
              >
                <span className={S.optionCopy}>
                  <Text as="span" size="2" weight="medium">
                    {itemMeta.title}
                  </Text>
                  <Text as="span" size="1" color="gray">
                    {itemMeta.description}
                  </Text>
                </span>
                <span className={S.optionBadge}>{itemMeta.short}</span>
              </button>
            );
          })}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default LanguageSwitcher;
